import { useCallback, useState } from "react";
import { useDropzone } from "react-dropzone";
import { cn } from "@/lib/utils";
import { Upload } from "lucide-react";

const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20MB
const ALLOWED_TYPES = [
  "image/jpeg",
  "image/png",
  "image/gif",
  "image/webp",
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "text/plain",
  "text/csv",
];

interface MessageInputDropzoneProps {
  onFilesDropped: (files: File[]) => void;
  disabled?: boolean;
  children: React.ReactNode;
}

export const MessageInputDropzone = ({
  onFilesDropped,
  disabled,
  children,
}: MessageInputDropzoneProps) => {
  const [isDraggingOver, setIsDraggingOver] = useState(false);

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (disabled) return;
      setIsDraggingOver(false);

      // Filter valid files
      const validFiles = acceptedFiles.filter((file) => {
        if (file.size > MAX_FILE_SIZE) {
          return false;
        }
        if (!ALLOWED_TYPES.includes(file.type)) {
          return false;
        }
        return true;
      });

      if (validFiles.length > 0) {
        onFilesDropped(validFiles);
      }
    },
    [onFilesDropped, disabled]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    onDragEnter: () => setIsDraggingOver(true),
    onDragLeave: () => setIsDraggingOver(false),
    noClick: true,
    noKeyboard: true,
    disabled,
  });

  return (
    <div {...getRootProps()} className="relative">
      <input {...getInputProps()} />
      
      {/* Drag overlay */}
      {(isDragActive || isDraggingOver) && (
        <div className="absolute inset-0 z-20 flex flex-col items-center justify-center rounded-lg border-2 border-dashed border-primary bg-primary/10 backdrop-blur-sm">
          <Upload className="h-8 w-8 text-primary mb-2" />
          <p className="text-sm font-medium text-primary">Drop files here to attach</p>
          <p className="text-xs text-muted-foreground mt-1">Images, PDFs, documents (max 20MB)</p>
        </div>
      )}

      {children}
    </div>
  );
};

export { MAX_FILE_SIZE, ALLOWED_TYPES };
